import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Search, Eye, Calendar, X, Check, CreditCard, Trash2 } from 'lucide-react';
import api from '../utils/api';

const statusLabels = {
  pending: { label: 'Ожидает', class: 'bg-yellow-500/20 text-yellow-400' },
  paid: { label: 'Оплачен', class: 'bg-blue-500/20 text-blue-400' },
  completed: { label: 'Завершён', class: 'bg-green-500/20 text-green-400' },
  cancelled: { label: 'Отменён', class: 'bg-red-500/20 text-red-400' },
  expired: { label: 'Истёк', class: 'bg-gray-500/20 text-gray-400' },
};

function OrderDetailModal({ order, onClose, onAction }) {
  if (!order) return null;
  const st = order.status;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-gray-800 rounded-2xl border border-gray-700 max-w-lg w-full max-h-[80vh] overflow-y-auto p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">Ордер #{order.id || order.orderId}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">&times;</button>
        </div>
        <div className="space-y-3 text-sm">
          {Object.entries(order).map(([key, value]) => {
            if (value === null || value === undefined || typeof value === 'object') return null;
            return (
              <div key={key} className="flex justify-between border-b border-gray-700/50 pb-2">
                <span className="text-gray-400">{key}</span>
                <span className="text-white text-right max-w-[60%] break-all">{String(value)}</span>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-gray-700">
          {(st === 'pending' || st === 'processing') && (
            <button
              onClick={() => onAction(order.id || order.orderId, 'paid')}
              className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <CreditCard size={14} /> Подтвердить оплату
            </button>
          )}
          {st === 'paid' && (
            <button
              onClick={() => onAction(order.id || order.orderId, 'completed')}
              className="flex items-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-500 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <Check size={14} /> Завершить
            </button>
          )}
          {st !== 'completed' && st !== 'cancelled' && (
            <button
              onClick={() => onAction(order.id || order.orderId, 'cancelled')}
              className="flex items-center gap-1.5 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs font-medium rounded-lg transition-colors"
            >
              <X size={14} /> Отменить
            </button>
          )}
          <button
            onClick={() => onAction(order.id || order.orderId, 'delete')}
            className="flex items-center gap-1.5 px-3 py-2 bg-red-600/20 hover:bg-red-600/40 text-red-400 text-xs font-medium rounded-lg transition-colors ml-auto"
          >
            <Trash2 size={14} /> Удалить
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OrdersScreen() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = statusFilter === 'paid'
        ? await api.getPaidOrders()
        : await api.getOrders(statusFilter || undefined);
      setOrders(data.orders || data.data || data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const viewOrder = async (orderId) => {
    try {
      const data = await api.getOrder(orderId);
      setSelectedOrder(data.order || data);
    } catch (err) {
      setError(`Ошибка загрузки ордера: ${err.message}`);
    }
  };

  const handleAction = async (orderId, action) => {
    const labels = { paid: 'оплаченным', completed: 'завершённым', cancelled: 'отменённым', delete: 'удалён' };
    const confirmMsg = action === 'delete'
      ? `Удалить ордер #${orderId}?`
      : `Отметить ордер #${orderId} как ${labels[action]}?`;
    if (!window.confirm(confirmMsg)) return;

    setActionLoading(orderId);
    setError(null);
    setSuccess(null);
    try {
      if (action === 'delete') {
        await api.deleteOrder(orderId);
        setSuccess(`Ордер #${orderId} удалён`);
      } else {
        await api.updateOrderStatus(orderId, action);
        setSuccess(`Ордер #${orderId} → ${labels[action]}`);
      }
      setSelectedOrder(null);
      await loadOrders();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const filteredOrders = useMemo(() => {
    const fromTs = dateFrom ? new Date(dateFrom).getTime() : 0;
    const toTs = dateTo ? new Date(dateTo + 'T23:59:59').getTime() : Infinity;

    return orders.filter(order => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const match =
          String(order.id || order.orderId || '').toLowerCase().includes(q) ||
          String(order.userId || order.user_id || '').includes(q) ||
          String(order.username || '').toLowerCase().includes(q) ||
          String(order.phone || '').includes(q);
        if (!match) return false;
      }
      if (fromTs || toTs < Infinity) {
        const raw = order.createdAt || order.created_at;
        if (!raw) return false;
        const ts = new Date(raw).getTime();
        if (ts < fromTs || ts > toTs) return false;
      }
      return true;
    });
  }, [orders, searchQuery, dateFrom, dateTo]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      });
    } catch { return dateStr; }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Ордера</h2>
        <button
          onClick={loadOrders}
          disabled={loading}
          className="p-2 bg-gray-800 rounded-lg border border-gray-700 text-gray-400 hover:text-white disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="flex bg-gray-800 rounded-lg border border-gray-700/50 overflow-hidden">
          {[
            { value: '', label: 'Все' },
            { value: 'pending', label: 'Ожидают' },
            { value: 'paid', label: 'Оплачены' },
            { value: 'completed', label: 'Завершены' },
            { value: 'cancelled', label: 'Отменены' },
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                statusFilter === f.value
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Поиск по ID, user_id, username..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          />
        </div>
      </div>

      {/* Date range */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Calendar size={14} className="text-gray-500" />
        <input
          type="date"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          className="bg-gray-800 border border-gray-700/50 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-purple-500 [color-scheme:dark]"
        />
        <span className="text-gray-500 text-xs">—</span>
        <input
          type="date"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          className="bg-gray-800 border border-gray-700/50 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-purple-500 [color-scheme:dark]"
        />
        {(dateFrom || dateTo) && (
          <button
            onClick={() => { setDateFrom(''); setDateTo(''); }}
            className="p-1.5 text-gray-400 hover:text-white transition-colors"
            title="Сбросить даты"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-4 text-green-400 text-sm">
          {success}
        </div>
      )}

      {/* Orders table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-xs border-b border-gray-700">
                <th className="text-left p-3">ID</th>
                <th className="text-left p-3">User</th>
                <th className="text-left p-3">Метод</th>
                <th className="text-right p-3">Сумма</th>
                <th className="text-right p-3">Профит</th>
                <th className="text-center p-3">Статус</th>
                <th className="text-right p-3">Дата</th>
                <th className="text-center p-3">Действия</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-t border-gray-700/50">
                    <td colSpan={8} className="p-3">
                      <div className="h-5 bg-gray-700 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-gray-500">
                    Ордера не найдены
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order, i) => {
                  const st = order.status;
                  const status = statusLabels[st] || { label: st, class: 'bg-gray-500/20 text-gray-400' };
                  const oid = order.id || order.orderId;
                  const isActing = actionLoading === oid;
                  return (
                    <tr key={oid || i} className="border-t border-gray-700/50 hover:bg-gray-700/30">
                      <td className="p-3 text-white font-mono text-xs">
                        {String(oid || '').slice(0, 8)}
                      </td>
                      <td className="p-3 text-gray-300 text-xs">
                        {order.username || order.userId || order.user_id || '—'}
                      </td>
                      <td className="p-3 text-gray-300 text-xs">
                        {order.paymentMethod || order.method || '—'}
                      </td>
                      <td className="p-3 text-right text-white">
                        {order.amount || order.amountCNY || '—'} {order.currency || 'CNY'}
                      </td>
                      <td className="p-3 text-right">
                        {order.profitUSDT != null ? (
                          <span className="text-green-400 font-medium">{order.profitUSDT.toFixed(2)} $</span>
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.class}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="p-3 text-right text-gray-400 text-xs">
                        {formatDate(order.createdAt || order.created_at)}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => viewOrder(oid)}
                            className="p-1 text-gray-400 hover:text-purple-400 transition-colors"
                            title="Подробнее"
                          >
                            <Eye size={14} />
                          </button>
                          {(st === 'pending' || st === 'processing') && (
                            <button
                              onClick={() => handleAction(oid, 'paid')}
                              disabled={isActing}
                              className="p-1 text-gray-400 hover:text-blue-400 transition-colors disabled:opacity-50"
                              title="Подтвердить оплату"
                            >
                              <CreditCard size={14} />
                            </button>
                          )}
                          {st === 'paid' && (
                            <button
                              onClick={() => handleAction(oid, 'completed')}
                              disabled={isActing}
                              className="p-1 text-gray-400 hover:text-green-400 transition-colors disabled:opacity-50"
                              title="Завершить"
                            >
                              <Check size={14} />
                            </button>
                          )}
                          <button
                            onClick={() => handleAction(oid, 'delete')}
                            disabled={isActing}
                            className="p-1 text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50"
                            title="Удалить"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order count + profit sum */}
      {!loading && (
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-gray-500">
            Всего: {filteredOrders.length} ордеров
          </p>
          {(() => {
            const sum = filteredOrders.reduce((s, o) => s + (o.profitUSDT || 0), 0);
            return sum > 0 ? (
              <p className="text-xs font-medium text-green-400">
                Профит: {sum.toFixed(2)} USDT
              </p>
            ) : null;
          })()}
        </div>
      )}

      {selectedOrder && (
        <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} onAction={handleAction} />
      )}
    </div>
  );
}
